<script>
    import {
        appStateStore,
        processInspectionStore,
        inspectionWaitingStore,
        fetchProcessInspectionData,
        fetchProcessInspectionWaitingData,
        fetchClassifications,
        inspectionClassStore,
    } from "./store.js";
    import NavBar from "../../components/NavBar.svelte";
    import ProcessInspectionSearch from "./ProcessInspectionSearch.svelte";
    import * as api from "../../utiles/api.js";
    import toast from "svelte-french-toast";
    import ProcessInspectionList from "./ProcessInspectionList.svelte";
    import ProcessInspectionDetail from "./ProcessInspectionDetail.svelte";
    import ProcessInspectionWaiting from "./ProcessInspectionWaiting.svelte";
    import BarcodeScannerModal from "../../components/BarcodeScannerModal.svelte";
    import { onMount, tick } from "svelte";
    import { Preferences } from "@capacitor/preferences";

    let isScanning = false;
    let scannerRef;

    let session_bplc_code = "";

    async function getData(silent = false) {
        // 💡 click 이벤트의 MouseEvent 객체가 silent 매개변수로 전달되는 것을 방지하여 수동 조회 시 토스트 정상 출력 보장
        const isSilent = silent === true;
        await fetchProcessInspectionData(session_bplc_code, isSilent);
    }

    function handleMenuReset() {
        appStateStore.set({ currentView: "list", selectedId: null });
    }

    function handleReset() {
        const today = new Date();
        const fourMonthsLater = new Date();

        today.setMonth(today.getMonth() - 4);
        fourMonthsLater.setMonth(today.getMonth() + 8);

        processInspectionStore.set({
            list: [],
            selectedItem: null,
            search: {
                prdctn_plan_no: "",
                lot_no: "",
                search_korean_nm: "",
                startDate: "2025-01-01",
                endDate: "2026-12-31",
                prpert_at: "전체",
                isExpanded: true,
            },
            scrollPos: 0,
        });

        appStateStore.set({
            currentView: "list",
            selectedId: null,
        });
        inspectionClassStore.update((s) => ({ ...s, selectedCode: "" }));

        tick().then(() => getData());
    }

    let barcodeBuffer = "";
    let lastKeyTime = 0;
    let isScanner = false;

    onMount(async () => {
        const { value } = await Preferences.get({ key: "retechInfo" });
        if (value) {
            const userData = JSON.parse(value);
            session_bplc_code = userData.bplc_code || "01";
        }

        // 화면에 진입할 때마다 최신 데이터로 새로고침 (최초 진입 시 조회 완료 토스트 숨김)
        getData(true);

        // 대기 리스트 미리 조회
        fetchProcessInspectionWaitingData(session_bplc_code, true);

        // 검사분류 미리 조회
        fetchClassifications(session_bplc_code);
    });

    // 💡 반환값: 카메라 스캐너 판정용 { success, message }
    async function handleBarcodeScan(barcode) {
        const loadingToast = toast.loading("바코드 검사대기 정보 조회 중...");
        try {
            const params = new URLSearchParams({
                bplc_code: session_bplc_code || "01",
                lot_no: barcode,
            });
            const response = await api.get(
                `process-inspection/waiting/list?${params.toString()}`,
            );
            if (response && response.data && response.data.length > 0) {
                // Exact match on LOT NO or ERP LOT NO
                const match = response.data.find(
                    (item) =>
                        item.lot_no === barcode ||
                        item.wrhousng_lot_no === barcode,
                );

                if (match) {
                    toast.dismiss(loadingToast);
                    appStateStore.set({
                        currentView: "detail",
                        selectedId: "new",
                        waitingData: match,
                    });
                    // Close waiting modal if open
                    inspectionWaitingStore.update((s) => ({
                        ...s,
                        modalOpen: false,
                    }));
                    return { success: true, message: "검사항목을 찾았습니다." };
                } else {
                    toast.dismiss(loadingToast);
                    return { success: false, message: "정확히 일치하는 LOT NO가 없습니다." };
                }
            } else {
                toast.dismiss(loadingToast);
                return { success: false, message: "대기 내역에서 일치하는 LOT NO를 찾을 수 없습니다." };
            }
        } catch (err) {
            console.error(err);
            toast.dismiss(loadingToast);
            return { success: false, message: "바코드 조회 중 오류가 발생했습니다." };
        }
    }

    function handleWindowKeydown(e) {
        // 상세 화면에서는 동작하지 않고 리스트 화면에서만 동작
        if ($appStateStore.currentView !== "list") return;

        const currentTime = Date.now();
        const timeDiff = currentTime - lastKeyTime;
        lastKeyTime = currentTime;

        // Enter키나 13번 키코드 확인
        const isEnter = e.key === "Enter" || e.keyCode === 13;

        // 입력 간격이 50ms를 초과하면 새로운 타이핑 시작으로 간주
        if (timeDiff > 50) {
            barcodeBuffer = "";
            isScanner = false;
        }

        // Enter키 입력 시 스캐너인지 최종 확인
        if (isEnter) {
            if (barcodeBuffer.length > 3 && (timeDiff <= 50 || isScanner)) {
                e.preventDefault();
                e.stopPropagation();
                if (document.activeElement && typeof document.activeElement.blur === 'function') {
                    document.activeElement.blur();
                }

                const scannedBarcode = barcodeBuffer;
                barcodeBuffer = "";
                isScanner = false;

                if (!$inspectionClassStore.selectedCode) {
                    toast.error("검사분류를 먼저 선택해주세요.");
                    return;
                }

                handleBarcodeScan(scannedBarcode).then((result) => {
                    if (result.success) {
                        toast.success(result.message);
                    } else {
                        toast.error(result.message);
                    }
                });
            }
            return;
        }

        // 일반 문자열일 경우 버퍼에 추가
        if (e.key.length === 1) {
            barcodeBuffer += e.key;
            // 두 번째 글자부터는 스캐너로 간주
            if (timeDiff <= 50) {
                isScanner = true;
                if (document.activeElement && document.activeElement.tagName === 'BUTTON') {
                    document.activeElement.blur();
                }
            }
        }
    }
</script>

<svelte:window on:keydown|capture={handleWindowKeydown} />

<div class="page-container">
    <NavBar on:reset={handleReset} on:menuReset={handleMenuReset} />

    <div class="content-wrapper">
        {#if $appStateStore.currentView === "list"}
            <ProcessInspectionSearch {getData} />

            <main class="main-layout">
                <ProcessInspectionList on:openScanner={() => isScanning = true} />
            </main>

            {#if $inspectionWaitingStore.modalOpen}
                <ProcessInspectionWaiting />
            {/if}
        {:else}
            <!-- Detail Mode -->
            <ProcessInspectionDetail />
        {/if}
    </div>

    {#if isScanning}
        <BarcodeScannerModal
            bind:this={scannerRef}
            on:scan={async (e) => {
                const result = await handleBarcodeScan(e.detail);
                if (result.success) toast.success(result.message);
                scannerRef?.reportResult(result.success, result.message);
            }}
            on:close={() => isScanning = false}
        />
    {/if}
</div>

<style>
    .page-container {
        display: flex;
        flex-direction: column;
        width: 100vw;
        height: 100vh;
        background: #f1f5f9;
        overflow: hidden;
    }
    .content-wrapper {
        flex: 1;
        display: flex;
        flex-direction: column;
        overflow: hidden;
    }
    .main-layout {
        flex: 1;
        display: flex;
        flex-direction: column;
        padding: 10px;
        gap: 10px;
        overflow: hidden;
    }
</style>
